import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import { useQuery } from "react-query";

// AWS Authentication
import { Auth, Hub } from "aws-amplify";

import axiosInstance from "../api/axiosInstance";
import query from "../api/constants.api";
import { TCognitoData } from "../api/types";
import { AwsConfigAuth } from "../utils/config/auth";

type User = {
  username: string;
  password: string;
};

// 1. Create Context
const AuthContext = createContext<any>(null);

// 2. Create custom hook
export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};

// 3. Create Provider
export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState(null); // user normalized data
  const [userData, setUserData] = useState<TCognitoData | null>(null); // cognito user data
  const [isLogging, setIsLogging] = useState(false);
  const [isError, setIsError] = useState(false);

  // Get new token from service now using Cognito idToken
  const getUserToken = () => {
    const idToken = userData?.signInUserSession?.idToken?.jwtToken;
    return axiosInstance(
      "GET",
      null,
      null,
      null,
      null,
      "https://okuvoeaz9b.execute-api.eu-west-1.amazonaws.com/v3_0_01/servicenow/oauth/token",
      "Bearer " + idToken
    );
  };

  const { isLoading: userLoading } = useQuery("getUserToken", getUserToken, {
    enabled: !!userData,
    refetchOnWindowFocus: false,
    refetchInterval: 240000, // update token every 4 min
    onSuccess: (data) => {
      localStorage.setItem("sessionToken", data?.data.access_token);
      localStorage.setItem("refreshToken", data?.data.refresh_token);
    },
  });

  // Get user information from service now using the user "sys_id"
  const fetchUser = () => {
    const params = {
      sysparm_fields:
        "name,user_password,web_service_access_only,managed_domain,sys_domain,active,user_name,internal_integration_user,first_name,email,last_name,middle_name,preferred_language,sys_id",
      sys_id: userData?.sys_id,
      sysparm_display_value: true,
    };

    return axiosInstance("GET", query.path.user, null, params);
  };

  const { isLoading: tokenLoading } = useQuery("getUser", fetchUser, {
    enabled: !!localStorage.getItem("sessionToken") && !!userData,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      setUser(data?.data.result[0]);
    },
  });

  // Handle user authentication
  useEffect(() => {
    Hub.listen("auth", ({ payload: { event, data } }) => {
      setIsError(false);

      switch (event) {
        case "signIn":
        case "cognitoHostedUI":
          getUser().then((userData) =>
            setUserData({
              ...userData,
              sys_id: userData?.attributes["custom:sys_id"],
            })
          );
          break;
        case "signOut":
          setIsLogging(false);
          setUser(null);
          setUserData(null);
          Auth.signOut();
          break;
        case "signIn_failure":
        case "cognitoHostedUI_failure":
          setIsError(true);
          console.log("Sign in failure", data);
          break;
      }
    });

    getUser().then((userData) => {
      if (userData) {
        setUserData({
          ...userData,
          sys_id: userData?.attributes["custom:sys_id"],
        });
      }
    });
  }, []);

  // Get user from AWS-Cognito
  const getUser = () => {
    Auth.configure(AwsConfigAuth);

    return Auth.currentAuthenticatedUser()
      .then((userData) => userData)
      .catch(() => console.log("Not signed in"));
  };

  const login = async (user: User) => {
    Auth.configure(AwsConfigAuth);

    Auth.federatedSignIn({ customProvider: "NOVA-SAML" })
      .then((cred) => {
        // If success, you will get the AWS credentials
        console.log(cred);
        return Auth.currentAuthenticatedUser();
      })
      .then((user) => {
        // If success, the user object you passed in Auth.federatedSignIn
        console.log(user);
      })
      .catch((e) => {
        console.log(e);
      });

    await Auth.signIn(user.username, user.password);
  };

  const logout = async () => {
    setIsLogging(true);
    Auth.configure(AwsConfigAuth);
    localStorage.removeItem("sessionToken");
    localStorage.removeItem("refreshToken");
    await Auth.signOut();
  };

  const value = useMemo(() => {
    return {
      user,
      userData,
      login,
      logout,
      isLogging: isLogging || tokenLoading || userLoading,
      isError,
    };
  }, [user, userData, isLogging, tokenLoading, userLoading, isError]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
